import { Box, Typography } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { ViewErrorKind } from '../../utils/async-view/view-error-kind';
import { ViewErrorActions } from './ViewErrorActions';
import { ViewErrorDetails } from './ViewErrorDetails';
import { viewErrorPresentation } from './view-error-presentation';

interface ViewErrorStateProps {
  kind: ViewErrorKind;
  /** Raw error message, shown only inside the collapsible technical details. */
  detail?: string;
  onRetry?: () => void;
}

/**
 * Centered, translated state for a view whose data could not be loaded.
 * Replaces raw backend messages with copy and recovery actions that match the error kind.
 */
export function ViewErrorState({ kind, detail, onRetry }: ViewErrorStateProps) {
  const { icon: Icon, title, help } = viewErrorPresentation[kind];

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        py: 10,
        px: 2,
      }}
    >
      <Icon sx={{ fontSize: 72, color: 'text.disabled', opacity: 0.5, mb: 2 }} />

      {/* Constrains the copy to a readable line length. */}
      <Box sx={{ maxWidth: 440 }}>
        <Typography variant="h6" color="text.primary" gutterBottom>
          <FormattedMessage {...title} />
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          <FormattedMessage {...help} />
        </Typography>
      </Box>

      <ViewErrorActions kind={kind} onRetry={onRetry} />

      {detail && <ViewErrorDetails detail={detail} />}
    </Box>
  );
}
